import { useState } from 'react'

import { journalPayload, type JournalPayload } from '../../lib/capture/journal'
import {
  EMPTY_DRAFT,
  NUMERIC_FIELDS,
  QUALITY_LABELS,
  STATE_OPTIONS,
  draftErrors,
  type DraftField,
  type JournalQuality,
  type JournalState,
  type NumericField,
  type SelfReportDraft,
} from '../../lib/selfReport'
import { CaffeineFields } from '../journal/CaffeineFields'
import { ChoiceChips } from '../journal/ChoiceChips'
import { ClockField } from '../journal/ClockField'
import { ExerciseFields } from '../journal/ExerciseFields'
import { JournalGroup } from '../journal/JournalGroup'
import { TagChips } from '../journal/TagChips'
import { Button, NumberStepper, TextArea } from '../ui'

/**
 * The journal asked BEFORE processing — "captured after the recording, before
 * the reveal, so it stays a clean validation label". Same vocabulary and same
 * controls as `SelfReportForm`, but it patches nothing: it hands back a
 * `journal.json` payload (or null for a skip) and the session builder ships it
 * in the archive. Nothing here is required.
 */
const STRESS_OPTIONS = Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
const QUALITY_OPTIONS = (Object.keys(QUALITY_LABELS) as JournalQuality[]).map((value) => ({ value, label: QUALITY_LABELS[value] }))
/**
 * Caffeine has left this list: it is asked with "Last cup" now, by
 * `CaffeineFields`, which owns the pair and its lock. Water stays here because
 * it is a plain count, and alcohol moves to its own group — it is the one
 * intake question about yesterday rather than this morning.
 */
const SINCE_WAKING: readonly NumericField[] = ['water_glasses']

export function CaptureJournal({ onDone }: { onDone: (payload: JournalPayload | null) => void }) {
  const [draft, setDraft] = useState<SelfReportDraft>(EMPTY_DRAFT)
  const [touched, setTouched] = useState<ReadonlySet<DraftField>>(new Set())
  const set = <K extends DraftField>(field: K, value: SelfReportDraft[K]) => {
    setDraft((d) => ({ ...d, [field]: value }))
    setTouched((t) => (t.has(field) ? t : new Set(t).add(field)))
  }
  const errors = draftErrors(draft, touched)
  const invalid = Object.keys(errors).length > 0
  const payload = journalPayload(draft)

  const numeric = (field: NumericField, hint?: string) => (
    <NumberStepper
      key={field}
      label={NUMERIC_FIELDS[field].label}
      value={draft[field]}
      onChange={(next) => set(field, next)}
      min={NUMERIC_FIELDS[field].min}
      max={'max' in NUMERIC_FIELDS[field] ? (NUMERIC_FIELDS[field] as { max: number }).max : undefined}
      step={NUMERIC_FIELDS[field].step}
      unit={NUMERIC_FIELDS[field].unit}
      hint={hint}
      error={errors[field] ?? null}
    />
  )

  const intake = (field: NumericField) => (
    <div className="journal__intake" key={field}>
      {numeric(field)}
      {draft[field] !== '0' && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => set(field, '0')}>
          None<span className="visually-hidden"> — {NUMERIC_FIELDS[field].label}</span>
        </button>
      )}
    </div>
  )

  return (
    <div className="journal journal--capture">
      {/*
        ── FLAT GROUPS, NOT AN ACCORDION (Sprint 1.9.11, concept §A) ──────────
        The four `<details>` sections here were an economy: five groups of
        questions on a phone, collapsed so the screen was not a wall. Schema 2
        makes them wrong. A collapsed group hides its own time anchor, and the
        anchor is the half of the question that makes the answer usable at all —
        "Exercised" under a closed "Movement" summary is the schema-1 question
        again. The concept draws them open, one group per screen height, and the
        scroll is the economy instead.
      */}
      <div className="journal__body">
        <JournalGroup title="How you feel" anchor="right now">
          <ChoiceChips
            label="Mood"
            options={STATE_OPTIONS}
            value={draft.state}
            onChange={(next) => set('state', next as JournalState | null)}
            hint="Your own words for right now, not a reading from the recording."
          />
          <ChoiceChips label="Stress 1-10" layout="scale" options={STRESS_OPTIONS} value={draft.stress_1_10} onChange={(next) => set('stress_1_10', next)} />
        </JournalGroup>

        <JournalGroup title="Sleep" anchor="last night">
          <div className="journal__group journal__group--grid">
            {numeric('sleep_hours', 'To the nearest quarter hour.')}
            <ChoiceChips label="Sleep quality" options={QUALITY_OPTIONS} value={draft.sleep_quality} onChange={(next) => set('sleep_quality', next as JournalQuality | null)} />
            <ClockField
              value={draft.woke_at_local}
              onChange={(next) => set('woke_at_local', next)}
              error={errors.woke_at_local ?? null}
            />
          </div>
        </JournalGroup>

        <JournalGroup title="Today, before this recording" anchor="since waking">
          <ExerciseFields draft={draft} set={set} minutesError={errors.exercise_minutes ?? null} />
          <CaffeineFields draft={draft} set={set} cupsError={errors.caffeine_cups ?? null} />
          <div className="journal__group journal__group--grid journal__group--intake">
            {SINCE_WAKING.map(intake)}
          </div>
        </JournalGroup>

        <JournalGroup title="Yesterday evening and last night" anchor="last 24 h">
          <div className="journal__group journal__group--grid journal__group--intake">
            {intake('alcohol_drinks')}
          </div>
          <TagChips value={draft.tags} onChange={(next) => set('tags', next)} />
        </JournalGroup>

        <JournalGroup title="Anything notable">
          <TextArea label="Notes" rows={3} value={draft.notes} onChange={(event) => set('notes', event.target.value)} />
        </JournalGroup>

        <div className="journal__actions">
          <Button type="button" variant="primary" disabled={invalid || payload === null} onClick={() => onDone(payload)}>
            Save and continue
          </Button>
          <Button type="button" variant="ghost" onClick={() => onDone(null)}>
            Skip
          </Button>
          <span className="journal__status">{payload === null ? 'Nothing answered yet — skipping writes no journal.' : 'Travels with the recording.'}</span>
        </div>
      </div>
    </div>
  )
}
